// Footage-compose channel (doc/tfx-footage-compose-design.md §1.1): an
// uploaded clip becomes a footage/1 package, optionally with AI-composed
// effects stacked around it. The pure payload logic lives here so the create
// page stays a form and the budget/round-trip rules are testable.
import type { TfxSpec } from '../api';

/** Closed set mirrored from TfxValidation.FOOTAGE_COMPOSE; the API and the
 *  worker both re-validate it. Absent on a queued row means "plain". */
export type FootageCompose = 'plain' | 'ai';

/**
 * The FOOTAGE job options this console authors. A refine re-posts all of it
 * verbatim, because the worker pins the clip, its placement and its licence
 * from the human-typed form — the model never touches them.
 */
export interface FootageJobOptions {
  refs: string[];
  name: string;
  blend: string;
  loop: boolean;
  place: { preset: string };
  license: { spdx: string; provenance: string };
  compose?: FootageCompose;
}

/** The trailing line an AI-composed footage job carries so the preview-content
 *  pick reaches the worker (`choose_template` resolves the base clip from
 *  `source`) and the reviewer — plain jobs send the bare hint as `source`. */
const PREVIEW_TEMPLATE_LABEL = 'Preview template:';
const PREVIEW_TEMPLATE_PREFIX = `${PREVIEW_TEMPLATE_LABEL} `;

/** One routing-marker line, matched on the trimmed text so a CRLF source or a
 *  stray indent is still recognised on the way back in. */
function isPreviewTemplateLine(line: string): boolean {
  return line.trim().startsWith(PREVIEW_TEMPLATE_LABEL);
}

/**
 * `source` for an AI-composed footage job: the description of the effects to
 * stack, plus the preview-content hint on its own trailing line. Plain jobs
 * send the bare hint instead (today's payload).
 */
export function composeFootageSource(description: string, previewHint: string): string {
  return `${description.trim()}\n${PREVIEW_TEMPLATE_PREFIX}${previewHint}`;
}

/**
 * The preview base a footage job was queued against, read back off its
 * `source`, so an iteration renders over the SAME clip as its parent instead
 * of falling back to the worker's default template.
 */
export function previewHintFromSource(source?: string | null): string | null {
  const lines = (source ?? '').split('\n');
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i]!.trim();
    if (isPreviewTemplateLine(line)) {
      const hint = line.slice(PREVIEW_TEMPLATE_LABEL.length).trim();
      if (hint) return hint;
    }
  }
  return null;
}

/**
 * The same `source` read as PROSE: every routing-marker line removed. The
 * marker is this console's own addressing to `choose_template`, not part of
 * what the admin asked for, and a refine quotes the root's source back to the
 * model as "The original request that produced it was: ..."
 * (`worker.py:2704-2707`) — a "Preview template: city" tail in that sentence
 * reads as a demand for the model's spec. Undefined when nothing but marker
 * lines is left.
 */
export function promptWithoutPreviewHint(source?: string | null): string | undefined {
  const prose = (source ?? '')
    .split('\n')
    .filter((line) => !isPreviewTemplateLine(line))
    .join('\n')
    .trim();
  return prose || undefined;
}

/**
 * `prompt-refs/<uuid>/` — the fixed head of every key the API mints
 * (admin-api `TfxValidation.PROMPT_REF_KEY`): the literal prefix, a 36-char
 * UUID and the separating slash.
 */
const REF_KEY_PREFIX_LEN = 'prompt-refs/'.length + 36 + 1;

/**
 * The longest ref key a FOOTAGE job may carry. `sanitizeRefName` on its own
 * allows a 100-character basename — a ~153-character key — and EVERY version
 * of the effect re-posts that key inside the 2000-character options budget it
 * shares with the licence provenance and the whole parent spec. Capping it
 * here is what keeps the worst case the form can actually queue (600-char
 * provenance, 64-char SPDX, 31-char name, six-step stack, six params)
 * refinable at all; see the budget test.
 */
export const MAX_FOOTAGE_REF_KEY_LEN = 90;

/**
 * Cut an already-sanitized ref filename (api.ts `sanitizeRefName`) down until
 * `prompt-refs/<uuid>/<name>` fits MAX_FOOTAGE_REF_KEY_LEN. The extension is
 * kept whole and the basename keeps the shape the API demands
 * (`PROMPT_REF_FILENAME`: leading alphanumeric, `[A-Za-z0-9._-]` after it, no
 * ".."), including the trailing rule `sanitizeRefName` applies BEFORE its own
 * slice — cutting mid-name can leave exactly the "." or "-" that the
 * mint-side check rejects, and a rejected filename fails the upload.
 */
export function capFootageRefName(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1) : '';
  // sanitizeRefName only ever emits png/jpg/webp/mp4/mov, so this is >= 36.
  const budget = MAX_FOOTAGE_REF_KEY_LEN - REF_KEY_PREFIX_LEN - 1 - ext.length;
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .slice(0, Math.max(budget, 1))
    .replace(/[.-]+$/, '');
  return ext ? `${base || 'ref'}.${ext}` : base || 'ref';
}

/** The raw JSON shape, cast the same way templateText/footageText cast theirs
 *  (the options column is a plain string; nothing here trusts its contents). */
interface RawFootageOptions {
  refs?: unknown;
  name?: unknown;
  blend?: unknown;
  loop?: unknown;
  place?: { preset?: unknown };
  license?: { spdx?: unknown; provenance?: unknown };
  compose?: unknown;
}

/**
 * The ROOT job's options, parsed for re-posting on a refine. Returns null when
 * the blob is not a complete footage payload: an iteration that cannot carry
 * the clip, its placement and its licence forward must never be offered, since
 * the worker would mint a package with no rights attached.
 */
export function parseFootageJobOptions(optionsJson?: string | null): FootageJobOptions | null {
  if (!optionsJson) return null;
  let raw: RawFootageOptions;
  try {
    const parsed: unknown = JSON.parse(optionsJson);
    // The literal "null" parses fine and then throws on the first field read,
    // which would take the whole jobs list down with it; a scalar has no
    // fields either. Both are "not a footage payload", not a crash.
    if (typeof parsed !== 'object' || parsed === null) return null;
    raw = parsed;
  } catch {
    return null;
  }
  const refs = Array.isArray(raw.refs)
    ? raw.refs.filter((ref): ref is string => typeof ref === 'string' && ref !== '')
    : [];
  const preset = raw.place?.preset;
  const spdx = raw.license?.spdx;
  const provenance = raw.license?.provenance;
  if (
    refs.length === 0 ||
    typeof raw.name !== 'string' ||
    typeof raw.blend !== 'string' ||
    typeof preset !== 'string' ||
    typeof spdx !== 'string' ||
    typeof provenance !== 'string'
  ) {
    return null;
  }
  return {
    refs,
    name: raw.name,
    blend: raw.blend,
    // Only an explicit false is one-shot, matching the worker's default.
    loop: raw.loop !== false,
    place: { preset },
    license: { spdx, provenance },
    ...(raw.compose === 'ai' ? { compose: 'ai' as const } : {}),
  };
}

/**
 * The parent spec as a refine re-posts it: WITHOUT the two blocks the worker
 * pins itself on every version — the licence, and the uploaded-clip step.
 *
 * Both are read back off the payload's OWN fields by `_pin_footage_spec`, so
 * the copies inside the parent spec are never used; they only reach the model,
 * as the example it is told to start from. That makes them worse than dead
 * weight, because the spec the console has in hand is the POST-pin spec.json
 * the worker uploaded as the `spec` artifact:
 *
 * - `license`: a 600-character provenance sent twice spent roughly a third of
 *   the options budget on the same text, and put a long licence plus a
 *   multi-step stack out of reach of a refine at all.
 * - the footage step: pinned it reads
 *   `{type, asset, kernelId, uniforms, loop, place}` (~190 characters more
 *   than the bare step, depending on the asset name), and
 *   `check_spec(footage=True)` rejects EVERY key but `type` on it —
 *   "stack[i] is the uploaded-clip step and must be exactly
 *   {"type": "footage"}". An example carrying the pinned keys contradicts the
 *   grammar from the freshest instruction in the prompt ("start from it"), so
 *   a model that copies it burns an attempt, and a model that keeps copying it
 *   burns the whole retry budget and fails the job.
 *
 * `version` stays: check_spec requires it on every generated spec, and this
 * JSON is the example the model answers in — dropping it buys ~20 characters
 * and risks a retry on a spec that came back without one.
 */
function withoutWorkerPinnedFields(parentSpec: TfxSpec): TfxSpec {
  const stripped: TfxSpec = { ...parentSpec };
  delete (stripped as Record<string, unknown>).license;
  const stack = stripped.stack;
  if (Array.isArray(stack)) {
    // A new array and a new step object: this spec is the query cache's copy.
    stripped.stack = (stack as unknown[]).map((step) =>
      isFootageStep(step) ? { type: 'footage' } : step,
    );
  }
  return stripped;
}

/** The uploaded-clip step, in either of the two shapes it reaches us in: bare
 *  as the model wrote it (design §1.2), or pinned as the worker rewrote it. */
function isFootageStep(step: unknown): boolean {
  return (
    typeof step === 'object' && step !== null && (step as { type?: unknown }).type === 'footage'
  );
}

/**
 * A refine job's options: the ROOT footage payload verbatim plus `refine`.
 * `compose` is forced to "ai" because only an AI-composed root is refinable.
 *
 * The options budget is shared with the licence provenance text and the whole
 * parent spec, so the dead weight goes first (what the worker re-pins anyway:
 * the parent's licence and the uploaded-clip step's pinned keys), then the
 * original prompt (as on the PROMPT channel); the rest of the parent spec is
 * non-negotiable, it pins the effect id. Returns null when even the
 * prompt-less form busts the budget — the caller turns that into an
 * actionable error.
 *
 * `parentPrompt` is the ROOT job's `source`, which on this channel ends with
 * the `Preview template:` routing line — stripped here, because the worker
 * quotes this string to the model as the original request.
 */
export function buildFootageRefineOptions(
  root: FootageJobOptions,
  parentSpec: TfxSpec,
  parentPrompt: string | undefined,
  maxLen: number,
): string | null {
  const base: FootageJobOptions = { ...root, compose: 'ai' };
  const spec = withoutWorkerPinnedFields(parentSpec);
  const prompt = promptWithoutPreviewHint(parentPrompt);
  if (prompt) {
    const withPrompt = JSON.stringify({
      ...base,
      refine: { parentSpec: spec, parentPrompt: prompt },
    });
    if (withPrompt.length <= maxLen) return withPrompt;
  }
  const bare = JSON.stringify({ ...base, refine: { parentSpec: spec } });
  return bare.length <= maxLen ? bare : null;
}
